import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { chartNames, strCode } from "../../../../consts";
import { dashboardUtil, rumEventsUtil } from "../../../../utils";
import { useSelectedAppTypes } from "../../../../hooks";

import {
  DashboardBarChart,
  DashboardLineChart,
  DashboardNumberChart,
  FlexContainers,
  RadialChart,
  WrapperContainer
} from "../../../../components";
import { DashboardTemplate } from "../../../../templates";
import { GridItem } from "../styled";
import DeviceConnectionsCountChart from "../defaultCharts/DeviceConnectionsCountChart";

const OverviewSection = ({
  getChartFilter = () => {},
  getIsLoading = () => {},
  getIsError = () => {}
}) => {
  const pageLoads = useSelector((store) => store.rum.pageLoads);
  const connectionTime = useSelector((store) => store.rum.connectionTime);
  const connectionsByMarket = useSelector((store) => store.rum.connectionsByMarket);
  const connectionsByBrowser = useSelector((store) => store.rum.connectionsByBrowser);
  const deviceUsage = useSelector((store) => store.rum.deviceUsage);
  const connectionsByOS = useSelector((store) => store.rum.connectionsByOS);

  const selectedAppTypes = useSelectedAppTypes();

  return (
    <>
      <GridItem gridColumn={"1 / 7"}>
        <WrapperContainer Container={FlexContainers.FlexRowBetween} gap={16}>
          {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.PAGE_LOADS) && (
            <DashboardTemplate
              id={chartNames.PAGE_LOADS}
              title={strCode.PAGE_LOADS_TITLE}
              isLoading={getIsLoading(chartNames.PAGE_LOADS)}>
              <DashboardNumberChart
                id={chartNames.PAGE_LOADS}
                value={pageLoads?.total}
                data={pageLoads?.loads}
                isLoading={getIsLoading(chartNames.PAGE_LOADS)}
                isError={getIsError(chartNames.PAGE_LOADS)}
                metricsConfig={rumEventsUtil.getMetricsConfig({
                  chartName: chartNames.PAGE_LOADS
                })}
              />
            </DashboardTemplate>
          )}

          {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.AVG_PAGE_LOAD_SPEED) && (
            <DashboardTemplate
              id={chartNames.AVG_PAGE_LOAD_SPEED}
              title={strCode.AVG_PAGE_LOAD_SPEED_TITLE}
              isLoading={getIsLoading(chartNames.AVG_PAGE_LOAD_SPEED)}>
              <DashboardNumberChart
                id={chartNames.AVG_PAGE_LOAD_SPEED}
                value={pageLoads?.avgSpeed && `${pageLoads.avgSpeed} ms`}
                isLoading={getIsLoading(chartNames.AVG_PAGE_LOAD_SPEED)}
                isError={getIsError(chartNames.AVG_PAGE_LOAD_SPEED)}
              />
            </DashboardTemplate>
          )}
        </WrapperContainer>
      </GridItem>

      {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.CONNECTION_TIME) && (
        <GridItem gridColumn={"1 / 7"}>
          <DashboardTemplate
            id={chartNames.CONNECTION_TIME}
            title={strCode.CONNECTION_TIME_TITLE}
            isLoading={getIsLoading(chartNames.CONNECTION_TIME)}>
            <DashboardLineChart
              id={chartNames.CONNECTION_TIME}
              data={connectionTime}
              isLoading={getIsLoading(chartNames.CONNECTION_TIME)}
              isError={getIsError(chartNames.CONNECTION_TIME)}
              yAxisLabel={strCode.MAX_AVG_TIME_LABEL}
              yAxisUnit="s"
              metricsConfig={rumEventsUtil.getMetricsConfig({
                chartName: chartNames.CONNECTION_TIME,
                deviceType: getChartFilter(chartNames.CONNECTION_TIME),
                coloredMetrics: {
                  mono: "mono",
                  duo: "duo"
                },
                updateMetricNameOnFilter: true
              })}
              deleteNullMetrics
            />
          </DashboardTemplate>
        </GridItem>
      )}

      {dashboardUtil.isChartAvailable(
        selectedAppTypes,
        chartNames.IQOS_DEVICE_CONNECTIONS_COUNT
      ) && (
        <GridItem gridColumn={"1 / 7"}>
          <DeviceConnectionsCountChart
            connectionsByMarket={connectionsByMarket}
            getChartFilter={getChartFilter}
            getIsLoading={getIsLoading}
            getIsError={getIsError}
          />
        </GridItem>
      )}

      {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.IQOS_DEVICE_USAGE) && (
        <GridItem gridColumn={"1 / 7"}>
          <DashboardTemplate
            id={chartNames.IQOS_DEVICE_USAGE}
            title={strCode.IQOS_DEVICE_USAGE}
            isLoading={getIsLoading(chartNames.IQOS_DEVICE_USAGE)}>
            <DashboardBarChart
              data={deviceUsage}
              isLoading={getIsLoading(chartNames.IQOS_DEVICE_USAGE)}
              isError={getIsError(chartNames.IQOS_DEVICE_USAGE)}
              yAxisLabel={strCode.COUNT_LABEL}
              metricsConfig={rumEventsUtil.getMetricsConfig({
                chartName: chartNames.IQOS_DEVICE_USAGE,
                deviceType: getChartFilter(chartNames.IQOS_DEVICE_USAGE)
              })}
              isCustomTicks
              deleteNullMetrics
            />
          </DashboardTemplate>
        </GridItem>
      )}

      <GridItem gridColumn={"1 / 7"}>
        <WrapperContainer Container={FlexContainers.FlexRowBetween} gap={16}>
          {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.OS) && (
            <DashboardTemplate
              id={chartNames.OS}
              title={strCode.OPERATING_SYSTEM_USAGE_TITLE}
              isLoading={getIsLoading(chartNames.OS)}>
              <RadialChart
                data={connectionsByOS?.statistics}
                title={strCode.TOTAL_TITLE}
                radius={120}
                strokeWidth={15}
                Container={FlexContainers.FlexRow}
                isLoading={getIsLoading(chartNames.OS)}
                isColumnLegend
                metricsConfig={rumEventsUtil.getMetricsConfig({
                  chartName: chartNames.OS,
                  deviceType: getChartFilter(chartNames.OS)
                })}
              />
            </DashboardTemplate>
          )}

          {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.BROWSER) && (
            <DashboardTemplate
              id={chartNames.BROWSER}
              title={strCode.BROWSER_USAGE_TITLE}
              isLoading={getIsLoading(chartNames.BROWSER)}>
              <RadialChart
                data={connectionsByBrowser?.statistics}
                title={strCode.TOTAL_TITLE}
                radius={120}
                strokeWidth={15}
                Container={FlexContainers.FlexRow}
                isLoading={getIsLoading(chartNames.BROWSER)}
                isColumnLegend
                metricsConfig={rumEventsUtil.getMetricsConfig({
                  chartName: chartNames.BROWSER,
                  deviceType: getChartFilter(chartNames.BROWSER)
                })}
              />
            </DashboardTemplate>
          )}
        </WrapperContainer>
      </GridItem>
    </>
  );
};

OverviewSection.propTypes = {
  getChartFilter: PropTypes.func.isRequired,
  getIsLoading: PropTypes.func.isRequired,
  getIsError: PropTypes.func.isRequired
};

export default OverviewSection;
